import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { CachedCredentialDto } from '@terra/shared-contracts-authenticator';
import { cachedCredentialsClient } from '@terra/shared-rest-clients-authenticator';
import { useProjectStore } from '../store/projectStore';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';

const EMPTY_ARRAY: CachedCredentialDto[] = [];

type UseCachedCredentialsOptions = {
  applicationId?: string;
  enabled?: boolean;
};

async function fetchCachedCredentials({
  applicationId,
}: {
  applicationId: string;
}): Promise<CachedCredentialDto[]> {
  const authHeaders = await getAuthHeaders();
  const response = await cachedCredentialsClient.getCachedCredentials({
    params: { applicationId },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/ai`,
    },
    extraHeaders: {
      ...authHeaders,
    },
  });

  if (response.status !== 200) {
    throw new Error('Failed to fetch cached credentials');
  }

  return response.body.credentials;
}

async function refreshCredential({
  applicationId,
  credentialId,
  sessionId,
}: {
  applicationId: string;
  credentialId: string;
  sessionId: string;
}): Promise<{ credential: CachedCredentialDto; message: string }> {
  const authHeaders = await getAuthHeaders();
  const response = await cachedCredentialsClient.refreshCredential({
    params: { applicationId, credentialId },
    body: { sessionId },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/ai`,
    },
    extraHeaders: {
      ...authHeaders,
    },
  });

  if (response.status !== 200) {
    throw new Error('Failed to refresh credential');
  }

  return {
    credential: response.body.credential,
    message: response.body.message,
  };
}

export function useCachedCredentials({
  applicationId,
  enabled = true,
}: UseCachedCredentialsOptions) {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;

  const query = useQuery({
    queryKey: ['cached-credentials', applicationId, tenantId],
    queryFn: async (): Promise<CachedCredentialDto[]> => {
      if (!applicationId || !tenantId) {
        return [];
      }
      return fetchCachedCredentials({ applicationId });
    },
    enabled: enabled && !!applicationId && !!tenantId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  const credentials = useMemo(() => query.data ?? EMPTY_ARRAY, [query.data]);

  return {
    credentials,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useRefreshCredential() {
  const queryClient = useQueryClient();
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;

  return useMutation({
    mutationFn: async ({
      applicationId,
      credentialId,
      sessionId,
    }: {
      applicationId: string;
      credentialId: string;
      sessionId: string;
    }) => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      return refreshCredential({
        applicationId,
        credentialId,
        sessionId,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['cached-credentials', variables.applicationId, tenantId],
      });
    },
  });
}
